import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Logo, ArrowLeftIcon, CheckIcon } from '../UI/Icons';

const highlights = [
  'Your transcript, parsed into a real plan',
  'Live Waterloo course data every term',
  'Advice grounded in the courses you have taken',
];

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to login. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#E9E9EE] p-3 sm:p-6">
      <div className="mx-auto grid min-h-[calc(100vh-1.5rem)] max-w-[1400px] overflow-hidden rounded-3xl bg-white shadow-shell sm:min-h-[calc(100vh-3rem)] lg:grid-cols-2">
        {/* ---- Brand panel ---- */}
        <div className="mesh-gradient relative hidden flex-col justify-between p-10 lg:flex">
          <Link to="/" className="flex items-center gap-3">
            <Logo size={42} />
            <span className="font-display text-xl font-semibold tracking-tight text-canvas-900">
              UW Pathfinder
            </span>
          </Link>

          <div>
            <h2 className="max-w-md font-display text-4xl font-bold leading-[1.1] tracking-[-0.02em] text-canvas-800">
              Plan your degree with real academic insight
            </h2>
            <ul className="mt-8 space-y-3">
              {highlights.map((item) => (
                <li key={item} className="flex items-center gap-3 text-sm text-canvas-700">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/70 text-primary-700 shadow-pill">
                    <CheckIcon width={13} height={13} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <p className="text-xs text-canvas-600">
            An independent student project · Not affiliated with the University of Waterloo
          </p>
        </div>

        {/* ---- Form panel ---- */}
        <div className="flex items-center justify-center px-5 py-12 sm:px-12">
          <div className="w-full max-w-sm">
            <Link
              to="/"
              className="mb-8 inline-flex items-center gap-1.5 text-sm text-canvas-600 transition-colors hover:text-canvas-900 lg:mb-10"
            >
              <ArrowLeftIcon width={16} height={16} />
              Back to home
            </Link>

            <div className="lg:hidden">
              <Logo size={44} />
            </div>

            <h1 className="mt-5 font-display text-3xl font-bold tracking-tight text-canvas-900 lg:mt-0">
              Welcome back
            </h1>
            <p className="mt-2 text-sm text-canvas-600">Sign in to pick up where you left off.</p>

            <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
              {error && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="email" className="label-field">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field"
                  placeholder="you@uwaterloo.ca"
                />
              </div>

              <div>
                <label htmlFor="password" className="label-field">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field"
                  placeholder="••••••••"
                />
              </div>

              <button type="submit" disabled={loading} className="btn-primary w-full py-3">
                {loading ? 'Signing in…' : 'Sign in'}
              </button>
            </form>

            <p className="mt-7 text-center text-sm text-canvas-600">
              Don&apos;t have an account?{' '}
              <Link to="/register" className="font-semibold text-primary-700 hover:text-primary-800">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
